import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  fetchSessionTranscriptAction,
  fetchSessionTranscriptSummaryAction,
} from '../../../app/page/session-transcript/transcripts.actions';
import { ISession } from '../../../app/resource/session/session.types';
import { useSelect, useSelectWithParams } from '../../../utils/selector.utils';
import { pageStateSelectors } from '../../../app/page/page-state/page-state.selectors';
import { WidgetWrapper } from '../common/widgets/WidgetWrapper';
import { transcriptPageSelectors } from '../../../app/page/session-transcript/transcripts.selectors';
import { sessionTranscriptSummarySelectors } from '../../../app/resource/session-transcript-summary/session-transcript-summary.selectors';
import { initialMeetingDashboardPage } from '../../../app/page/meeting-dashboard/meeting-dashboard.actions';
import { IMeetingSimple } from '../../../app/resource/meeting-simple/meeting-simple.types';
import { addSecondsAndFormatTime, convertAndFormatDateToUserTimeZone } from '../../../utils/date.utils';
import { authSelectors } from '../../../app/auth/auth.selectors';
import { userSelectors } from '../../../app/resource/user/user.selectors';
import { PageLayout } from './PageLayout';

const MeetingTranscriptPage = () => {
  const dispatch = useDispatch();
  const { meetingId, sessionId } = useParams<{ meetingId: IMeetingSimple['id']; sessionId: ISession['id'] }>();

  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');
  const showLoader = useSelectWithParams(pageStateSelectors.selectPageInProgress, 'sessionTranscripts');
  const transcript = useSelectWithParams(transcriptPageSelectors.selectSessionTranscriptsBySessionId, sessionId);
  const transcriptSummary = useSelectWithParams(sessionTranscriptSummarySelectors.selectResourceById, sessionId);

  const currentPageRef = useRef<number | null>(null);
  const totalPagesRef = useRef<number | null>(null);
  const loadingRef = useRef<boolean | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const transcriptPaneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    currentPageRef.current = transcript?.currentPage ?? null;
    totalPagesRef.current = transcript?.totalPages ?? null;
    loadingRef.current = showLoader;
  }, [transcript, showLoader]);

  useEffect(() => {
    if (meetingId) {
      dispatch(initialMeetingDashboardPage(meetingId));
    }

    dispatch(fetchSessionTranscriptAction(sessionId));
    dispatch(fetchSessionTranscriptSummaryAction(sessionId));
  }, [dispatch, sessionId, meetingId]);

  const transcriptContentLength = transcript?.content?.length ?? 0;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const transcriptPane = transcriptPaneRef.current;
    if (!sentinel || !transcriptPane) {
      return;
    }

    const loadNextPage = () => {
      const currentPage = currentPageRef.current;
      const totalPages = totalPagesRef.current;
      const pageToFetch = (currentPage ?? 0) + 1;

      if (currentPage && totalPages && currentPage < totalPages && loadingRef.current === false) {
        dispatch(fetchSessionTranscriptAction(sessionId, { page: pageToFetch }));
      }
    };

    let observer: IntersectionObserver | null = null;

    const disconnectObserver = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };

    const createObserver = () => {
      disconnectObserver();
      const isDesktop = window.matchMedia('(min-width: 901px)').matches;
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            loadNextPage();
          }
        },
        { root: isDesktop ? transcriptPane : null, rootMargin: '100px', threshold: 0 },
      );
      observer.observe(sentinel);
    };

    createObserver();

    const mediaQuery = window.matchMedia('(min-width: 901px)');
    const handleMediaChange = () => {
      createObserver();
    };

    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      disconnectObserver();
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, [dispatch, sessionId, transcriptContentLength]);

  const getWidgetTitle = useCallback(() => {
    if (!transcriptSummary || !loggedUser) {
      return '';
    }
    const formattedDate = convertAndFormatDateToUserTimeZone(
      transcriptSummary.sessionDate,
      loggedUser.timezone,
      'MMMM DD, YYYY',
    );
    return `Meeting Transcript - ${transcriptSummary.meetingTitle} - ${formattedDate}`;
  }, [transcriptSummary, loggedUser]);

  if (!loggedUser) {
    return (
      <PageLayout showLoader={true} smallSidebar>
        <span className="mb-1 css-loader--meeting-dashboard-navbar"></span>
      </PageLayout>
    );
  }

  return (
    <PageLayout showLoader={showLoader} sectionClassName="meeting-transcript-page__section" smallSidebar>
      <div className="meeting-transcript-page__content">
        <WidgetWrapper name={getWidgetTitle()}>
          <div className="meeting-transcript-page">
            <div className="meeting-transcript-page__pane meeting-transcript-page__pane--transcript">
              <div className="panel vto-text meeting-transcript-page__panel">
                <div className="panel-header small-header no-border pb-0 mb-1 meeting-transcript-page__panel-header">
                  <h3 className="bold">Transcript</h3>
                </div>
                <div ref={transcriptPaneRef} className="meeting-transcript-page__panel-body">
                  {transcript?.content?.length ? (
                    transcript.content.map((sessionTranscript) => {
                      const time =
                        loggedUser.timezone &&
                        transcriptSummary?.startedDate &&
                        addSecondsAndFormatTime(
                          transcriptSummary.startedDate,
                          sessionTranscript.startedAt,
                          loggedUser.timezone,
                        );

                      return (
                        <div className="meeting-transcript-entry border-bottom" key={sessionTranscript.id}>
                          <div className="meeting-transcript-entry__row">
                            {time && <time className="meeting-transcript-entry__time">{time}</time>}
                            <div className="meeting-transcript-entry__content">
                              <span className="bold meeting-transcript-entry__name">
                                {sessionTranscript.participantName}
                              </span>
                              <p className="meeting-transcript-entry__message">{sessionTranscript.message}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center my-4">
                      <p style={{ color: '#666', fontStyle: 'italic' }}>
                        {showLoader ? 'Loading transcript...' : 'No transcript available for this meeting.'}
                      </p>
                    </div>
                  )}
                  {showLoader && <span className="mb-1 css-loader--meeting-dashboard-navbar"></span>}
                  <div ref={sentinelRef} className="meeting-transcript-page__sentinel" aria-hidden="true" />
                </div>
              </div>
            </div>

            <div className="meeting-transcript-page__pane meeting-transcript-page__pane--summary">
              <div className="panel vto-text meeting-transcript-page__panel">
                <div className="panel-header small-header no-border pb-0 mb-1 meeting-transcript-page__panel-header">
                  <h3 className="bold">Summary</h3>
                </div>
                <div className="meeting-transcript-page__panel-body">
                  {transcriptSummary?.summary ? (
                    <div
                      className="meeting-transcript-summary"
                      dangerouslySetInnerHTML={{ __html: transcriptSummary.summary }}
                    />
                  ) : (
                    <div className="text-center my-4">
                      <p style={{ color: '#666', fontStyle: 'italic' }}>
                        {showLoader ? 'Generating summary...' : 'No summary available for this meeting.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </WidgetWrapper>
      </div>
    </PageLayout>
  );
};

export default MeetingTranscriptPage;
